import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Checkbox } from '@/components/ui/checkbox';
import { Calendar, GripVertical, FileText } from 'lucide-react';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';
import type { WebsiteTask } from '@/types/website';
import { TASK_PRIORITY_LABELS, WEBSITE_LABELS } from '@/types/website';
import { cn } from '@/lib/utils';
import type { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';

export interface TaskCardProps {
  task: WebsiteTask;
  onClick?: () => void;
  isDragging?: boolean;
  showCheckbox?: boolean;
  isSelected?: boolean;
  onToggleSelect?: (e: React.MouseEvent) => void;
  dragListeners?: SyntheticListenerMap;
}

const priorityColors: Record<string, string> = {
  low: 'bg-muted text-muted-foreground',
  medium: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  high: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
  urgent: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
};

const websiteColors: Record<string, string> = {
  greenvision: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  gv_charging: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
};

export function TaskCard({ 
  task, 
  onClick, 
  isDragging, 
  showCheckbox, 
  isSelected, 
  onToggleSelect,
  dragListeners 
}: TaskCardProps) {
  const assignee = task.assigned_profile;
  const initials = assignee?.display_name
    ?.split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) || '?';

  const handleCardClick = (e: React.MouseEvent) => {
    if (showCheckbox && onToggleSelect) {
      onToggleSelect(e);
    } else if (onClick) {
      onClick();
    }
  };

  const handleCheckboxClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onToggleSelect) {
      onToggleSelect(e);
    }
  };

  return (
    <Card
      className={cn(
        'cursor-pointer hover:shadow-md transition-all',
        isDragging && 'opacity-50 rotate-2 shadow-lg',
        isSelected && 'ring-2 ring-primary'
      )}
      onClick={handleCardClick}
    >
      <CardContent className="p-3 space-y-2">
        <div className="flex items-start gap-2">
          {showCheckbox && (
            <div onClick={handleCheckboxClick} className="mt-0.5">
              <Checkbox checked={isSelected} />
            </div>
          )}
          <div 
            className="shrink-0 mt-0.5 cursor-grab active:cursor-grabbing" 
            {...dragListeners}
          >
            <GripVertical className="h-4 w-4 text-muted-foreground opacity-50 hover:opacity-100" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-sm line-clamp-2">{task.title}</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-1">
          <Badge variant="outline" className={cn('text-xs', websiteColors[task.website])}>
            {WEBSITE_LABELS[task.website]}
          </Badge>
          <Badge variant="outline" className={cn('text-xs', priorityColors[task.priority])}>
            {TASK_PRIORITY_LABELS[task.priority]}
          </Badge>
        </div>

        {task.page_tags && task.page_tags.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {task.page_tags.slice(0, 2).map((tag) => (
              <Badge key={tag} variant="outline" className="text-xs bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">
                <FileText className="h-2.5 w-2.5 mr-1" />
                {tag}
              </Badge>
            ))}
            {task.page_tags.length > 2 && (
              <Badge variant="outline" className="text-xs">
                +{task.page_tags.length - 2}
              </Badge>
            )}
          </div>
        )}

        {task.labels && task.labels.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {task.labels.slice(0, 3).map((label) => (
              <Badge key={label} variant="secondary" className="text-xs">
                {label}
              </Badge>
            ))}
            {task.labels.length > 3 && (
              <Badge variant="secondary" className="text-xs">
                +{task.labels.length - 3}
              </Badge>
            )}
          </div>
        )}

        <div className="flex items-center justify-between pt-1">
          {task.due_date && (
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <Calendar className="h-3 w-3" />
              {format(new Date(task.due_date), 'd MMM', { locale: nl })}
            </div>
          )}
          {assignee && (
            <Avatar className="h-6 w-6">
              <AvatarFallback className="text-xs bg-primary/10 text-primary">
                {initials}
              </AvatarFallback>
            </Avatar>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
